/**
 * File Utilities for Solar Financial Model
 * Handles CSV import/export of model parameters
 */

let exportInProgress = false;

/**
 * Exports model inputs to CSV file
 */
export const exportParametersToCSV = (inputs, filename = 'solar_model_parameters.csv') => {
    if (exportInProgress) return;
    exportInProgress = true;
    
    try {
        const header = Object.keys(inputs).join(',');
        const values = Object.values(inputs).join(',');
        const csvContent = `${header}\n${values}`;
        
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        
        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        }
    } catch (error) {
        console.error('Export error:', error);
        alert('Error exporting parameters: ' + error.message);
    } finally {
        setTimeout(() => { exportInProgress = false; }, 1000);
    }
};

/**
 * Imports model parameters from CSV file
 */
export const importParametersFromCSV = (file, setInputs) => {
    return new Promise((resolve, reject) => {
        // Validate file
        const validation = validateImportFile(file);
        if (!validation.valid) {
            alert(validation.message);
            reject(new Error(validation.message));
            return;
        }
        
        const reader = new FileReader();
        
        reader.onerror = () => {
            const error = 'Error reading file';
            alert(error);
            reject(new Error(error));
        };
        
        reader.onload = (e) => {
            try {
                const result = parseCSVContent(e.target.result);
                
                if (result.success) {
                    if (setInputs) {
                        setInputs(prev => ({ ...prev, ...result.inputs }));
                    }
                    resolve({
                        success: true,
                        inputs: result.inputs,
                        message: `Successfully imported ${Object.keys(result.inputs).length} parameters`
                    });
                } else {
                    alert('Error parsing CSV: ' + result.message);
                    reject(new Error(result.message));
                }
            } catch (error) {
                const errorMessage = 'Error parsing CSV: ' + error.message;
                alert(errorMessage);
                reject(new Error(errorMessage));
            }
        };
        
        reader.readAsText(file);
    });
};

/**
 * Validates import file before processing
 */
const validateImportFile = (file) => {
    if (!file) {
        return { valid: false, message: 'No file selected' };
    }
    
    if (!file.name.endsWith('.csv')) {
        return { valid: false, message: 'Please select a valid CSV file' };
    }
    
    if (file.size > 1024 * 1024) { // 1MB limit
        return { valid: false, message: 'File size must be under 1MB' };
    }
    
    return { valid: true };
};

/**
 * Parses CSV content and extracts valid parameters
 */
const parseCSVContent = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    
    if (lines.length < 2) {
        return {
            success: false,
            message: 'Invalid CSV format - must have header and data rows'
        };
    }
    
    const header = lines[0].split(',').map(h => h.trim());
    const values = lines[1].split(',').map(v => v.trim());
    
    if (header.length !== values.length) {
        return {
            success: false,
            message: 'CSV format error - header and data row lengths do not match'
        };
    }
    
    const newInputs = {};
    let validCount = 0;
    let invalidCount = 0;
    
    header.forEach((key, index) => {
        const value = parseNumericValue(values[index]);
        if (value !== null && key) {
            newInputs[key] = value;
            validCount++;
        } else {
            invalidCount++;
        }
    });
    
    if (validCount === 0) {
        return {
            success: false,
            message: 'No valid numeric parameters found in CSV'
        };
    }
    
    return {
        success: true,
        inputs: newInputs,
        validCount,
        invalidCount
    };
};

/**
 * Safely parses numeric values from CSV
 */
const parseNumericValue = (value) => {
    if (!value || value === '') return null;
    
    const num = Number(value);
    return (!isNaN(num) && isFinite(num)) ? num : null;
};

/**
 * Triggers file input dialog for import
 */
export const triggerFileImport = (fileInputRef) => {
    if (fileInputRef && fileInputRef.current) {
        fileInputRef.current.click();
    }
};

/**
 * Handles file input change event
 */
export const handleFileInputChange = async (event, setInputs) => {
    const file = event.target.files[0];
    if (file) {
        try {
            const result = await importParametersFromCSV(file, setInputs);
            console.log('Import successful:', result.message);
            return result;
        } catch (error) {
            console.error('Import failed:', error);
            return { success: false, message: error.message };
        } finally {
            // Clear the input to allow re-importing the same file
            event.target.value = '';
        }
    }
    return { success: false, message: 'No file selected' };
};

/**
 * Validates that imported parameters are within reasonable bounds
 */
export const validateImportedParameters = (inputs) => {
    const bounds = {
        installedCapacity: [0.1, 10000],
        annualHours: [0, 8760],
        onGridTariff: [0.001, 1.0],
        operationPeriod: [1, 50],
        constructionPeriod: [1, 10],
        investmentPerMW: [1000, 10000000],
        longTermLoanRate: [0, 0.5],
        discountRate: [0.01, 0.5],
        pvDegradationRate: [0, 0.1],
        operatingCostRate: [0, 1],
        profitDistributionRate: [0, 1]
    };
    
    const warnings = [];
    const errors = [];
    
    Object.entries(inputs).forEach(([key, value]) => {
        if (bounds[key]) {
            const [min, max] = bounds[key];
            if (value < min || value > max) {
                warnings.push(`${key}: ${value} is outside recommended range [${min}, ${max}]`);
            }
        }
        
        // Check for required parameters
        if (['installedCapacity', 'investmentPerMW', 'ownFunds'].includes(key) && (!value || value <= 0)) {
            errors.push(`${key} is required and must be greater than 0`);
        }
    });
    
    return {
        valid: errors.length === 0,
        errors,
        warnings
    };
};

/**
 * Exports financial results to CSV
 */
export const exportResultsToCSV = (outputs, filename = 'solar_model_results.csv') => {
    try {
        const indicators = outputs.indicators || {};
        const investmentPlan = outputs.investmentPlan || {};
        
        const data = {
            // Key Financial Indicators
            projectNPV: indicators.projectNPV || 0,
            projectIRR: indicators.projectIRR || 0,
            capitalNPV: indicators.capitalNPV || 0,
            capitalIRR: indicators.capitalIRR || 0,
            lcoe: indicators.lcoe || 0,
            paybackPeriod: indicators.paybackPeriod || 0,
            discountedPaybackPeriod: indicators.discountedPaybackPeriod || 0,
            
            // Investment Plan
            totalInvestment: investmentPlan.totalInvestment || 0,
            totalStaticInvestment: investmentPlan.totalStaticInvestment || 0,
            workingCapital: investmentPlan.workingCapital || 0,
            bessInvestment: investmentPlan.bessInvestment || 0,
            
            // Calculated totals
            totalNetProfit: outputs.pnlStatement?.reduce((a,b) => a + b.netProfit, 0) || 0,
            totalEVA: outputs.evaStatement?.reduce((a,b) => a + b.eva, 0) || 0,
            avgCostPerKwh: outputs.costStatement?.reduce((a,b) => a + b.costPerKwh, 0) / (outputs.costStatement?.length || 1) || 0
        };
        
        const header = Object.keys(data).join(',');
        const values = Object.values(data).join(',');
        const csvContent = `${header}\n${values}`;
        
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        
        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        }
    } catch (error) {
        console.error('Results export error:', error);
        alert('Error exporting results: ' + error.message);
    }
}; 